import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Plus, Play, Trash2, Edit3, Clock, Webhook, Mouse,
  CheckCircle, XCircle, Loader2, RefreshCw, ChevronRight,
  Activity, Calendar, Link2, ToggleLeft, ToggleRight, Settings,
  ArrowLeft, Sparkles, Sun, Moon, Workflow,
  Copy, StopCircle, PlayCircle, Ban, MoreHorizontal, X,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
  listWorkflows, deleteWorkflow, updateWorkflow, executeWorkflow,
  cloneWorkflow, listExecutionsFiltered, cancelExecution,
} from '../lib/workflowApi';
import CredentialManager from '../components/workflow/CredentialManager';

const TRIGGER_ICON = { manual: Mouse, webhook: Webhook, schedule: Clock };
const STATUS_STYLE = {
  success: { color: '#10B981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', label: 'Success' },
  failed:  { color: '#EF4444', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)',  label: 'Failed' },
  running: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', label: 'Running' },
  pending: { color: '#94A3B8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)', label: 'Pending' },
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Clone modal ───────────────────────────────────────────────────────────
function CloneModal({ workflow, isDark, onConfirm, onClose }) {
  const [name, setName] = useState(`${workflow.name} (copy)`);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr(null);
    try { await onConfirm(name.trim()); }
    catch (ex) { setErr(ex?.response?.data?.detail || 'Clone failed'); setBusy(false); }
  };

  const bg     = isDark ? '#0d1117' : '#fff';
  const border = isDark ? '#1F2937' : '#e2e8f0';
  const text1  = isDark ? '#E6EDF3' : '#0f172a';
  const text2  = isDark ? '#64748B' : '#64748b';

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 }}
      onClick={onClose}
    >
      <div
        style={{ background: bg, border: `1px solid ${border}`, borderRadius: 16, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 24px 48px rgba(0,0,0,0.35)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Copy size={16} color="#8B5CF6" />
            </div>
            <span style={{ fontSize: 16, fontWeight: 700, color: text1 }}>Clone Workflow</span>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, display: 'flex', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <p style={{ fontSize: 13, color: text2, marginBottom: 18, lineHeight: 1.6 }}>
          Cloning <strong style={{ color: text1 }}>"{workflow.name}"</strong>. All nodes and edges will be copied. Execution history is not transferred.
        </p>
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: text2, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>New Workflow Name</label>
          <input
            ref={inputRef}
            value={name}
            onChange={e => { setName(e.target.value); setErr(null); }}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 14px', borderRadius: 9,
              border: `1px solid ${err ? '#EF4444' : border}`, background: isDark ? '#0a0f18' : '#F8FAFC',
              color: text1, fontSize: 14, fontFamily: 'inherit', outline: 'none',
            }}
            placeholder="Enter workflow name…"
          />
          {err && <p style={{ color: '#EF4444', fontSize: 12, marginTop: 6 }}>{err}</p>}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={busy}
              style={{ padding: '9px 18px', borderRadius: 9, background: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9', border: `1px solid ${border}`, color: text2, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button type="submit" disabled={busy || !name.trim()}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 9, background: 'linear-gradient(135deg,#7C3AED,#6D28D9)', border: '1px solid rgba(139,92,246,0.5)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit' }}>
              {busy ? <Loader2 size={14} style={{ animation: 'spin 0.9s linear infinite' }} /> : <Copy size={14} />}
              {busy ? 'Cloning…' : 'Clone Workflow'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Toast notification ─────────────────────────────────────────────────────
function Toast({ message, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3200); return () => clearTimeout(t); }, [onDone]);
  const colors = { success: '#10B981', error: '#EF4444', info: '#8B5CF6' };
  const color = colors[type] || colors.info;
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 99999,
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '12px 18px', borderRadius: 12,
      background: type === 'error' ? 'rgba(239,68,68,0.12)' : type === 'success' ? 'rgba(16,185,129,0.12)' : 'rgba(139,92,246,0.12)',
      border: `1px solid ${color}40`, backdropFilter: 'blur(12px)',
      boxShadow: `0 8px 32px ${color}30`,
      animation: 'fadeUp 0.25s ease',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 13, fontWeight: 600, color }}>{message}</span>
    </div>
  );
}

// ── Actions dropdown per card ──────────────────────────────────────────────
function CardActionsMenu({ wf, isDark, onStop, onResume, onCancel, onClone, busy }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const bg     = isDark ? '#111827' : '#fff';
  const border = isDark ? '#1F2937' : '#e2e8f0';
  const text2  = isDark ? '#94A3B8' : '#64748b';

  const items = [
    { key: 'clone',  label: 'Clone',  Icon: Copy,        color: '#8B5CF6', action: onClone },
    wf.is_active
      ? { key: 'stop',   label: 'Stop',   Icon: StopCircle,  color: '#F59E0B', action: onStop }
      : { key: 'resume', label: 'Resume', Icon: PlayCircle,  color: '#10B981', action: onResume },
    ...(wf.last_execution_status === 'running'
      ? [{ key: 'cancel', label: 'Cancel Run', Icon: Ban, color: '#EF4444', action: onCancel }]
      : []),
  ];

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      <button
        title="More actions"
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        style={{ display: 'flex', alignItems: 'center', padding: '5px 7px', background: open ? (isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0') : (isDark ? 'rgba(255,255,255,0.04)' : '#F1F5F9'), border: `1px solid ${border}`, borderRadius: 8, color: text2, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' }}
      >
        {busy ? <Loader2 size={14} style={{ animation: 'spin 0.9s linear infinite' }} color={text2} /> : <MoreHorizontal size={14} />}
      </button>
      {open && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', right: 0,
            background: bg, border: `1px solid ${border}`, borderRadius: 10,
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.6)' : '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 160, zIndex: 9999, overflow: 'hidden',
          }}
        >
          {items.map(item => (
            <button
              key={item.key}
              onClick={e => { e.stopPropagation(); setOpen(false); item.action(e); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '9px 14px', background: 'transparent', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 500,
                color: item.color, textAlign: 'left', transition: 'background 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#F8FAFC'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
              <item.Icon size={14} />{item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WorkflowList() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [executingId, setExecutingId]   = useState(null);
  const [deletingId, setDeletingId]     = useState(null);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [cloneTarget, setCloneTarget]   = useState(null);
  const [toast, setToast]               = useState(null);
  const [showCreds, setShowCreds]       = useState(false);
  const [hoveredCard, setHoveredCard]   = useState(null);
  const [mounted, setMounted]           = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
  }, []);

  const bg       = isDark ? '#060B14' : '#F0F4F8';
  const surface  = isDark ? 'rgba(13,17,26,0.9)' : 'rgba(255,255,255,0.98)';
  const border   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.1)';
  const text1    = isDark ? '#F1F5F9' : '#0D1A3A';
  const text2    = isDark ? '#64748B' : '#475569';
  const gridLine = isDark ? 'rgba(139,92,246,0.08)' : 'rgba(99,102,241,0.07)';

  const load = async () => {
    setLoading(true); setError(null);
    try { const r = await listWorkflows(); setWorkflows(r.data); }
    catch { setError('Failed to load workflows. Please make sure you are logged in.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); setTimeout(() => setMounted(true), 60); }, []);

  const handleRun = async (e, id) => {
    e.stopPropagation(); setExecutingId(id);
    try { await executeWorkflow(id, {}); setTimeout(load, 1200); }
    catch {} finally { setTimeout(() => setExecutingId(null), 1200); }
  };

  const handleToggle = async (e, wf) => {
    e.stopPropagation();
    try {
      await updateWorkflow(wf.id, { is_active: !wf.is_active });
      setWorkflows(p => p.map(w => w.id === wf.id ? { ...w, is_active: !wf.is_active } : w));
    } catch {}
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this workflow? This cannot be undone.')) return;
    setDeletingId(id);
    try { await deleteWorkflow(id); setWorkflows(p => p.filter(w => w.id !== id)); }
    catch {} finally { setDeletingId(null); }
  };

  // ── Stop: deactivate the workflow (prevents future runs) ──────────────
  const handleStop = useCallback(async (e, wf) => {
    e.stopPropagation();
    setActionBusyId(wf.id);
    try {
      await updateWorkflow(wf.id, { is_active: false });
      setWorkflows(p => p.map(w => w.id === wf.id ? { ...w, is_active: false } : w));
      showToast(`"${wf.name}" stopped`, 'success');
    } catch {
      showToast('Failed to stop workflow', 'error');
    } finally {
      setActionBusyId(null);
    }
  }, [showToast]);

  // ── Resume: re-activate a stopped workflow ────────────────────────────
  const handleResume = useCallback(async (e, wf) => {
    e.stopPropagation();
    setActionBusyId(wf.id);
    try {
      await updateWorkflow(wf.id, { is_active: true });
      setWorkflows(p => p.map(w => w.id === wf.id ? { ...w, is_active: true } : w));
      showToast(`"${wf.name}" resumed`, 'success');
    } catch {
      showToast('Failed to resume workflow', 'error');
    } finally {
      setActionBusyId(null);
    }
  }, [showToast]);

  // ── Cancel: abort a currently running execution ───────────────────────
  const handleCancel = useCallback(async (e, wf) => {
    e.stopPropagation();
    setActionBusyId(wf.id);
    try {
      const r = await listExecutionsFiltered(wf.id, { status: 'running', limit: 1 });
      const running = r.data?.[0];
      if (!running) {
        showToast('No running execution found', 'info');
        return;
      }
      await cancelExecution(wf.id, running.id);
      setWorkflows(p => p.map(w => w.id === wf.id ? { ...w, last_execution_status: 'cancelled' } : w));
      showToast('Execution cancelled', 'success');
    } catch (ex) {
      showToast(ex?.response?.data?.detail || 'Failed to cancel execution', 'error');
    } finally {
      setActionBusyId(null);
    }
  }, [showToast]);

  // ── Clone: open name modal ────────────────────────────────────────────
  const handleCloneOpen = useCallback((e, wf) => {
    e.stopPropagation();
    setCloneTarget(wf);
  }, []);

  const handleCloneConfirm = useCallback(async (newName) => {
    const wf = cloneTarget;
    setActionBusyId(wf.id);
    try {
      await cloneWorkflow(wf.id, newName);
      setCloneTarget(null);
      showToast(`"${newName}" created`, 'success');
      // Refresh list to show the new clone
      const r = await listWorkflows();
      setWorkflows(r.data);
    } catch (ex) {
      setActionBusyId(null);
      throw ex; // bubble to modal for inline error display
    }
    setActionBusyId(null);
  }, [cloneTarget, showToast]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = [
    { label: 'Total Workflows', value: workflows.length,                                              color: '#8B5CF6', icon: Workflow  },
    { label: 'Active',          value: workflows.filter(w => w.is_active).length,                    color: '#10B981', icon: ToggleRight },
    { label: 'Scheduled',       value: workflows.filter(w => w.trigger_type === 'schedule').length,  color: '#F59E0B', icon: Calendar  },
    { label: 'Webhook',         value: workflows.filter(w => w.trigger_type === 'webhook').length,   color: '#06B6D4', icon: Link2     },
  ];

  return (
    <div style={{ minHeight: '100vh', background: bg, fontFamily: "'Inter', sans-serif", position: 'relative', overflow: 'hidden' }}>

      {/* ── Grid Background ── */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        backgroundImage: `linear-gradient(${gridLine} 1px, transparent 1px), linear-gradient(90deg, ${gridLine} 1px, transparent 1px)`,
        backgroundSize: '30px 30px',
        animation: 'gridScroll 16s linear infinite',
      }} />

      {/* Glow blobs */}
      <div style={{ position: 'fixed', top: '-15%', left: '5%', width: '55vw', height: '55vh', background: 'radial-gradient(ellipse, rgba(139,92,246,0.14) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', bottom: '0', right: '-5%', width: '45vw', height: '50vh', background: 'radial-gradient(ellipse, rgba(248,114,23,0.1) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '60vw', height: '60vh', background: isDark ? 'radial-gradient(ellipse, rgba(6,182,212,0.04) 0%, transparent 70%)' : 'none', pointerEvents: 'none', zIndex: 0 }} />

      {/* ── Content ── */}
      <div style={{
        position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '36px 40px 80px',
        opacity: mounted ? 1 : 0, transform: mounted ? 'none' : 'translateY(18px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}>

        {/* ── Header ── */}
        <div data-tour="workflow-list-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40, gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button
              onClick={() => navigate('/marketplace')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, background: surface, border: `1px solid ${border}`, color: text2, cursor: 'pointer', transition: 'all 0.18s', flexShrink: 0 }}
              title="Back to Marketplace"
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.5)'; e.currentTarget.style.color = '#8B5CF6'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = text2; }}
            >
              <ArrowLeft size={16} />
            </button>
            <div style={{ position: 'relative', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {/* Outer animated ring */}
              <div style={{ position: 'absolute', inset: 0, borderRadius: 16, border: '2px solid rgba(248,114,23,0.15)', borderTopColor: 'rgba(124,58,237,0.3)', animation: 'spin 4s linear infinite' }} />
              {/* Inner box */}
              <div style={{ width: 46, height: 46, borderRadius: 12, background: 'linear-gradient(135deg, rgba(248,114,23,0.15), rgba(124,58,237,0.1))', border: '1px solid rgba(248,114,23,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 30px rgba(248,114,23,0.3), inset 0 0 10px rgba(124,58,237,0.1)', backdropFilter: 'blur(5px)' }}>
                <Zap size={24} color="#F87217" style={{ filter: 'drop-shadow(0 0 6px rgba(248,114,23,0.6))' }} />
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h1 className="flux-text" style={{ fontSize: 36, fontWeight: 900, margin: 0, letterSpacing: '-1px', textTransform: 'uppercase', fontStyle: 'italic', paddingRight: 4 }}>
                  FLUX
                </h1>
                <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 100, fontSize: 10, fontWeight: 800, color: '#A78BFA', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Engine
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(217,119,6,0.1)', border: `1px solid ${isDark ? 'rgba(245,158,11,0.35)' : 'rgba(217,119,6,0.45)'}`, borderRadius: 100, fontSize: 10, fontWeight: 800, color: isDark ? '#FCD34D' : '#B45309', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: isDark ? '#F59E0B' : '#D97706', boxShadow: `0 0 6px ${isDark ? 'rgba(245,158,11,0.8)' : 'rgba(217,119,6,0.6)'}`, animation: 'pulse 2s ease-in-out infinite' }} />
                  Beta
                </span>
              </div>
              <p style={{ fontSize: 13, color: text2, margin: '2px 0 0', fontWeight: 500, letterSpacing: '0.2px' }}>AI-Powered Workflow Automation Engine</p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: 10, background: surface, border: `1px solid ${border}`, color: text2, cursor: 'pointer', transition: 'all 0.18s' }}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={load} style={{ ...ghostBtn(surface, border, text2), gap: 6 }}>
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={() => setShowCreds(true)} style={{ ...ghostBtn(surface, border, text2), gap: 6 }}>
              <Settings size={14} /> Credentials
            </button>
            <button data-tour="workflow-new-btn" onClick={() => navigate('/workflow/new')} style={createBtnStyle}>
              <Sparkles size={15} /> New Workflow
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div data-tour="workflow-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 40 }}>
          {stats.map(({ label, value, color, icon: Icon }, i) => (
            <div key={label} className="stat-card" style={{ ...statCard(surface, border), animationDelay: `${i * 70}ms` }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: `${color}18`, border: `1px solid ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: `0 0 18px ${color}30` }}>
                <Icon size={20} color={color} />
              </div>
              <div>
                <div style={{ fontSize: 30, fontWeight: 800, color: text1, lineHeight: 1, letterSpacing: '-1px' }}>{value}</div>
                <div style={{ fontSize: 12, color: text2, marginTop: 4, fontWeight: 500 }}>{label}</div>
              </div>
              {/* bottom glow */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: `radial-gradient(ellipse at 50% 100%, ${color}18 0%, transparent 70%)`, pointerEvents: 'none', borderRadius: '0 0 14px 14px' }} />
            </div>
          ))}
        </div>

        {/* ── Divider ── */}
        {!loading && !error && workflows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
            <div style={{ flex: 1, height: 1, background: border }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: text2, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Your Workflows</span>
            <div style={{ flex: 1, height: 1, background: border }} />
          </div>
        )}

        {/* ── Content ── */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0' }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Loader2 size={28} color="#8B5CF6" className="spin" />
            </div>
            <p style={{ color: text2, marginTop: 16, fontSize: 14 }}>Loading workflows…</p>
          </div>
        ) : error ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0' }}>
            <XCircle size={36} color="#EF4444" />
            <p style={{ color: text2, marginTop: 12, fontSize: 14 }}>{error}</p>
          </div>
        ) : workflows.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0' }}>
            <div style={{ width: 84, height: 84, borderRadius: 24, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 40px rgba(139,92,246,0.2)' }}>
              <Zap size={38} color="#8B5CF6" />
            </div>
            <h3 style={{ color: text1, fontWeight: 700, margin: '20px 0 8px', fontSize: 20 }}>No workflows yet</h3>
            <p style={{ color: text2, marginBottom: 28, fontSize: 14, maxWidth: 360, textAlign: 'center', lineHeight: 1.65 }}>
              Create your first workflow to start automating tasks with AI-powered nodes.
            </p>
            <button style={createBtnStyle} onClick={() => navigate('/workflow/new')}>
              <Plus size={16} /> Create First Workflow
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))', gap: 20 }}>
            {workflows.map((wf, idx) => {
              const TIcon = TRIGGER_ICON[wf.trigger_type] || Mouse;
              const ss = wf.last_execution_status ? (STATUS_STYLE[wf.last_execution_status] || STATUS_STYLE.pending) : null;
              const isHov = hoveredCard === wf.id;
              return (
                <div
                  key={wf.id}
                  className="wf-card"
                  style={{
                    ...cardBase(surface, border),
                    opacity: wf.is_active ? 1 : 0.55,
                    borderColor: isHov ? 'rgba(139,92,246,0.5)' : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'),
                    boxShadow: isHov ? '0 0 0 1px rgba(139,92,246,0.35), 0 12px 40px rgba(0,0,0,0.35)' : (isDark ? '0 2px 10px rgba(0,0,0,0.35)' : '0 2px 10px rgba(0,0,0,0.06)'),
                    transform: isHov ? 'translateY(-3px)' : 'none',
                    animationDelay: `${idx * 55}ms`,
                  }}
                  onClick={() => navigate(`/workflow/${wf.id}`)}
                  onMouseEnter={() => setHoveredCard(wf.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  {/* Accent top bar */}
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2.5, background: 'linear-gradient(90deg, #7C3AED, #F87217)', opacity: isHov ? 1 : 0, transition: 'opacity 0.22s', borderRadius: '16px 16px 0 0' }} />

                  {/* Card header */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                      <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(248,114,23,0.12)', border: '1px solid rgba(248,114,23,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Zap size={14} color="#F87217" />
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: text1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wf.name}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 9px', background: 'rgba(248,114,23,0.1)', border: '1px solid rgba(248,114,23,0.22)', borderRadius: 100, fontSize: 10, fontWeight: 600, color: '#F87217', textTransform: 'capitalize' }}>
                        <TIcon size={10} />{wf.trigger_type}
                      </span>
                      <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', color: wf.is_active ? '#10B981' : '#475569' }} onClick={e => handleToggle(e, wf)}>
                        {wf.is_active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                    </div>
                  </div>

                  {wf.description && <p style={{ fontSize: 13, color: text2, margin: 0, lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{wf.description}</p>}

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: text2, fontWeight: 500 }}><Activity size={11} />{wf.node_count} node{wf.node_count !== 1 ? 's' : ''}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: text2, fontWeight: 500 }}><Calendar size={11} />{formatDate(wf.updated_at)}</span>
                    {ss && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 100, fontSize: 10, fontWeight: 700, color: ss.color, background: ss.bg, border: `1px solid ${ss.border}` }}>
                        {wf.last_execution_status === 'success' && <CheckCircle size={9} />}
                        {wf.last_execution_status === 'failed'  && <XCircle    size={9} />}
                        {wf.last_execution_status === 'running' && <Loader2    size={9} className="spin" />}
                        {ss.label}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, paddingTop: 10, borderTop: `1px solid ${border}` }}>
                    {/* Edit */}
                    <button
                      title="Open in editor"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', background: isDark ? 'rgba(255,255,255,0.05)' : '#F1F5F9', border: `1px solid ${border}`, borderRadius: 8, color: text2, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
                      onClick={e => { e.stopPropagation(); navigate(`/workflow/${wf.id}`); }}
                    >
                      <Edit3 size={12} /> Edit
                    </button>

                    {/* Run */}
                    <button
                      title="Run workflow"
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', background: 'rgba(248,114,23,0.1)', border: '1px solid rgba(248,114,23,0.28)', borderRadius: 8, color: '#F87217', fontSize: 12, cursor: executingId === wf.id ? 'not-allowed' : 'pointer', fontWeight: 700, fontFamily: 'inherit', opacity: executingId === wf.id ? 0.6 : 1 }}
                      onClick={e => handleRun(e, wf.id)}
                      disabled={executingId === wf.id}
                    >
                      {executingId === wf.id ? <Loader2 size={12} className="spin" /> : <Play size={12} />} Run
                    </button>

                    {/* More actions dropdown: Clone / Stop / Resume / Cancel */}
                    <CardActionsMenu
                      wf={wf}
                      isDark={isDark}
                      busy={actionBusyId === wf.id}
                      onClone={e => handleCloneOpen(e, wf)}
                      onStop={e => handleStop(e, wf)}
                      onResume={e => handleResume(e, wf)}
                      onCancel={e => handleCancel(e, wf)}
                    />

                    {/* Delete */}
                    <button
                      title="Delete workflow"
                      style={{ display: 'flex', alignItems: 'center', padding: '5px 9px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, color: '#EF4444', cursor: deletingId === wf.id ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
                      onClick={e => handleDelete(e, wf.id)}
                      disabled={deletingId === wf.id}
                    >
                      {deletingId === wf.id ? <Loader2 size={12} className="spin" /> : <Trash2 size={12} />}
                    </button>
                    <ChevronRight size={14} color={text2} style={{ marginLeft: 'auto' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreds && <CredentialManager onClose={() => setShowCreds(false)} />}

      {/* Clone modal */}
      {cloneTarget && (
        <CloneModal
          workflow={cloneTarget}
          isDark={isDark}
          onConfirm={handleCloneConfirm}
          onClose={() => setCloneTarget(null)}
        />
      )}

      {/* Toast notifications */}
      {toast && (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onDone={() => setToast(null)}
        />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
        @keyframes gridScroll { from { background-position: 0 0; } to { background-position: 44px 44px; } }
        @keyframes gradientShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        
        .flux-text {
          background: linear-gradient(to right, #F87217, #FFB067, #7C3AED, #F87217);
          background-size: 300% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: gradientShift 6s linear infinite;
        }

        .spin { animation: spin 0.9s linear infinite; }
        .stat-card { animation: fadeUp 0.45s ease both; }
        .wf-card { animation: fadeUp 0.4s ease both; }
      `}</style>
    </div>
  );
}

const ghostBtn = (surface, border, color) => ({
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', background: surface, border: `1px solid ${border}`,
  borderRadius: 9, color, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
});

const createBtnStyle = {
  display: 'flex', alignItems: 'center', gap: 7,
  padding: '9px 18px',
  background: 'linear-gradient(135deg, #F87217, #EA580C)',
  border: '1px solid rgba(248,114,23,0.45)',
  borderRadius: 10, color: '#fff', fontSize: 13, fontWeight: 700,
  cursor: 'pointer', boxShadow: '0 0 22px rgba(248,114,23,0.35)',
  transition: 'all 0.15s', fontFamily: 'inherit',
};

const statCard = (surface, border) => ({
  display: 'flex', alignItems: 'center', gap: 16,
  padding: '22px 24px', background: surface, border: `1px solid ${border}`,
  borderRadius: 16, backdropFilter: 'blur(14px)',
  position: 'relative', overflow: 'hidden',
  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
});

const cardBase = (surface, border) => ({
  background: surface, border: `1px solid ${border}`,
  borderRadius: 16, padding: '18px 20px',
  cursor: 'pointer', transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
  display: 'flex', flexDirection: 'column', gap: 12,
  position: 'relative', overflow: 'hidden',
  backdropFilter: 'blur(10px)',
});